import { AnimationProps, Controller, SpringConfig, SpringRef } from '@react-spring/web';
import React from 'react';
import { Boundary, Rect } from '../types';
import { Nullable } from 'src/types/nullable.type';

export type BoundaryState = {
  x: number;
  y: number;
  width: number;
  height: number;
  opacity: number;
};

type ExtendedBoundary = Boundary & {
  active: boolean;
};

function pushBlockControllerUpdate(
  controller: Controller<BoundaryState>,
  props: Partial<BoundaryState>,
  animationProps: AnimationProps<BoundaryState>
) {
  let started = 0;
  let skipped = 0;
  const updates: Partial<BoundaryState> = {};
  const springs = controller.springs;
  for (const key in springs) {
    const rKey = key as keyof BoundaryState;
    if (!(rKey in props)) {
      continue;
    }
    if (springs[rKey].goal !== props[rKey]) {
      updates[rKey] = props[rKey];
      started++;
    } else {
      skipped++;
    }
  }
  controller.start({
    ...updates,
    ...animationProps,
  });
  return { started, skipped };
}

const ANIMATION_CONFIG: SpringConfig = {
  mass: 1,
  tension: 400,
  friction: 40,
};

function createController(): Controller<BoundaryState> {
  return new Controller({
    ref: SpringRef<BoundaryState>(),
    from: {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      opacity: 0,
    },
  });
}

type ControllersState = {
  controllers: [Controller<BoundaryState>, Controller<BoundaryState>];
  boundaries: [Nullable<ExtendedBoundary>, Nullable<ExtendedBoundary>];
  activeControllerIndex: number;
};

export function useAnimatedBoundaries(boundaries: Boundary[], activeBoundaryKey: Nullable<string>) {
  const controllersStateRef = React.useRef<ControllersState>({
    controllers: [createController(), createController()],
    boundaries: [null, null],
    activeControllerIndex: -1,
  });

  const extendedBoundaries = React.useMemo(() => {
    return boundaries.map((re) => ({
      ...re,
      active: re.boundaryKey === activeBoundaryKey,
    }));
  }, [boundaries, activeBoundaryKey]);

  const prevActiveBoundaryRef = React.useRef<ExtendedBoundary>();

  const springs = React.useMemo(() => {
    // console.log('useAnimatedBoundaries', 'prevBoundaries', prevBoundaries);

    const { activeControllerIndex } = controllersStateRef.current;

    let activeChanged = false;
    if (prevActiveBoundaryRef.current && !extendedBoundaries.find((b) => b.active)) {
      activeChanged = true;
    } else if (!prevActiveBoundaryRef.current && extendedBoundaries.find((b) => b.active)) {
      activeChanged = true;
    } else if (
      prevActiveBoundaryRef.current &&
      extendedBoundaries.find((b) => b.active) &&
      prevActiveBoundaryRef.current.boundaryKey !== extendedBoundaries.find((b) => b.active)?.boundaryKey
    ) {
      activeChanged = true;
    }

    const exitingBoundary = activeChanged && activeControllerIndex !== -1 ? prevActiveBoundaryRef.current : null;
    const enteringBoundary = activeChanged ? extendedBoundaries.find((b) => b.active) : null;

    prevActiveBoundaryRef.current = extendedBoundaries.find((b) => b.active) ?? undefined;

    // console.log(
    //   'useAnimatedBoundaries',
    //   'activeChanged',
    //   activeChanged,
    //   'exitingBoundary',
    //   exitingBoundary,
    //   'enteringBoundary',
    //   enteringBoundary,
    //   'prevActiveBoundaryRef.current',
    //   prevActiveBoundaryRef.current
    // );

    if (exitingBoundary) {
      const exitingControllerIndex = activeControllerIndex;
      const exitingController = controllersStateRef.current.controllers[exitingControllerIndex];
      pushBlockControllerUpdate(
        exitingController,
        {
          opacity: 0,
        },
        {
          config: ANIMATION_CONFIG,
        }
      );
    }
    if (enteringBoundary) {
      const enteringControllerIndex = activeControllerIndex === -1 ? 0 : 1 - activeControllerIndex;
      const enteringController = controllersStateRef.current.controllers[enteringControllerIndex];
      pushBlockControllerUpdate(
        enteringController,
        {
          x: enteringBoundary.x,
          y: enteringBoundary.y,
          width: enteringBoundary.width,
          height: enteringBoundary.height,
          opacity: 0,
        },
        {
          config: ANIMATION_CONFIG,
          immediate: true,
        }
      );
      pushBlockControllerUpdate(
        enteringController,
        {
          opacity: 1,
        },
        {
          config: ANIMATION_CONFIG,
        }
      );
      controllersStateRef.current.activeControllerIndex = enteringControllerIndex;
      controllersStateRef.current.boundaries[enteringControllerIndex] = enteringBoundary;
    }
    if (!activeChanged && activeControllerIndex !== -1) {
      const activeController = controllersStateRef.current.controllers[activeControllerIndex];
      const activeBoundary = controllersStateRef.current.boundaries[activeControllerIndex];
      if (activeBoundary) {
        pushBlockControllerUpdate(
          activeController,
          {
            x: activeBoundary.x,
            y: activeBoundary.y,
            width: activeBoundary.width,
            height: activeBoundary.height,
          },
          {
            config: ANIMATION_CONFIG,
          }
        );
      }
    }

    return controllersStateRef.current.controllers.map((controller) => controller.springs);
  }, [extendedBoundaries]);

  return { springs };
}
